'use client';

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

type ApiReachability =
  | { state: 'loading' }
  | { state: 'ok'; version?: string }
  | { state: 'blocked'; reason: string }   // likely client-side (ad blocker, extension, firewall)
  | { state: 'server-error'; status: number };

const Footer = () => {
  const [uiVersion, setUiVersion] = useState<string | null>(process.env.APP_VERSION || null);
  const [api, setApi] = useState<ApiReachability>({ state: 'loading' });
  const [detailOpen, setDetailOpen] = useState(false);

  const checkApi = useCallback(() => {
    setApi({ state: 'loading' });
    // Fast timeout — if this doesn't respond in a few seconds the user's
    // connection to /api is almost certainly blocked.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 6000);
    fetch('/api/health_checker', { signal: controller.signal })
      .then(async (res) => {
        clearTimeout(timeout);
        if (!res.ok) {
          setApi({ state: 'server-error', status: res.status });
          return;
        }
        const data = await res.json().catch(() => ({}));
        setApi({ state: 'ok', version: data?.version });
      })
      .catch((err) => {
        clearTimeout(timeout);
        const reason = err?.name === 'AbortError'
          ? 'timeout'
          : err instanceof TypeError
          ? 'network'   // "Failed to fetch" — browser didn't get a response at all
          : err?.message || 'unknown';
        setApi({ state: 'blocked', reason });
      });
  }, []);

  useEffect(() => {
    // Fetch the runtime version file (written during docker build).
    // This is a static file so it always matches the deployed image,
    // unlike process.env which is baked in at Next.js build time.
    fetch('/version.txt')
      .then((res) => res.ok ? res.text() : null)
      .then((text) => {
        const v = text?.trim();
        if (v) setUiVersion(v);
      })
      .catch(() => {});

    checkApi();
  }, [checkApi]);

  const apiBlocked = api.state === 'blocked' || api.state === 'server-error';

  return (
    <footer className="mt-auto w-full text-center p-4 bg-gray-200 dark:bg-gray-800 text-sm text-muted-foreground dark:text-gray-300">
      <p className="mb-2">&copy; {new Date().getFullYear()} Powston. All rights reserved.</p>
      <div className="space-x-4">
        <a href="https://powston.com.au" target="_blank" rel="noopener noreferrer" className="hover:underline">
          powston.com.au
        </a>
        <a href="https://www.powston.com.au/blog" target="_blank" rel="noopener noreferrer" className="hover:underline">
          Blog
        </a>
        {/* Public legal pages — kept as plain <a> so they work the same
            from authenticated and unauthenticated contexts (the Footer
            renders on the login screen too). */}
        <a href="/privacy" className="hover:underline">
          Privacy
        </a>
        <a href="/terms" className="hover:underline">
          Terms
        </a>
      </div>

      <p className="mt-2 text-xs text-muted-foreground/70 dark:text-gray-500 inline-flex items-center justify-center gap-1.5 flex-wrap">
        {uiVersion && <span>ui:{uiVersion}</span>}
        {uiVersion && api.state === 'ok' && <span>·</span>}
        {api.state === 'ok' && api.version && <span>api:{api.version}</span>}

        {apiBlocked && (
          <>
            <span className="hidden sm:inline">·</span>
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 hover:underline font-medium"
              title="Powston API not reachable — click for details"
            >
              <AlertTriangle className="w-3 h-3" />
              API unreachable
            </button>
          </>
        )}
      </p>

      {apiBlocked && detailOpen && (
        <ApiBlockedModal
          api={api}
          onClose={() => setDetailOpen(false)}
          onRetry={() => {
            checkApi();
            setDetailOpen(false);
          }}
        />
      )}
    </footer>
  );
};

const ApiBlockedModal: React.FC<{
  api: ApiReachability;
  onClose: () => void;
  onRetry: () => void;
}> = ({ api, onClose, onRetry }) => {
  const reason = api.state === 'blocked' ? api.reason : undefined;
  const isNetwork = reason === 'network' || reason === 'timeout';

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-card dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full p-6 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <h2 className="text-base font-semibold">Can&apos;t reach the Powston API</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-foreground mb-3 leading-relaxed">
          The page loaded, but requests to <code className="px-1 rounded bg-muted text-xs">/api/…</code>{' '}
          aren&apos;t getting through. Dashboard values may be blank or out of date.
        </p>

        {isNetwork && (
          <>
            <p className="text-sm font-medium text-foreground mb-1">Likely causes</p>
            <ul className="list-disc ml-5 space-y-1 text-sm text-muted-foreground mb-4">
              <li>
                An <strong>ad-blocker or privacy extension</strong> (e.g. uBlock Origin,
                AdGuard, Privacy Badger, Ghostery) blocking requests that match{' '}
                <code className="text-xs">/api/</code>.
              </li>
              <li>
                A <strong>corporate firewall or VPN</strong> blocking this domain.
              </li>
              <li>
                <strong>Browser tracking protection</strong> (Firefox Strict mode, Brave Shields, Safari).
              </li>
              <li>Your internet connection dropping briefly.</li>
            </ul>
            <p className="text-sm font-medium text-foreground mb-1">What to try</p>
            <ol className="list-decimal ml-5 space-y-1 text-sm text-muted-foreground mb-4">
              <li>
                Temporarily disable your ad-blocker or allow-list{' '}
                <code className="text-xs">app.powston.com</code> and try again.
              </li>
              <li>Open the page in a private / incognito window with extensions off.</li>
              <li>
                If you&apos;re on a corporate network, ask IT to allow{' '}
                <code className="text-xs">app.powston.com</code>.
              </li>
              <li>Try a different browser or network (e.g. phone hotspot) to confirm.</li>
            </ol>
          </>
        )}

        {api.state === 'server-error' && (
          <>
            <p className="text-sm text-foreground mb-1">
              The backend responded with status{' '}
              <code className="text-xs">{api.status}</code>.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              This usually means Powston is restarting or briefly unavailable. It should
              recover within a minute.
            </p>
          </>
        )}

        <div className="flex gap-2 justify-end pt-2 border-t border-border">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-muted"
          >
            Close
          </button>
          <button
            onClick={onRetry}
            className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Retry now
          </button>
        </div>

        {reason && (
          <p className="mt-3 text-[10px] text-muted-foreground/70 font-mono text-right">
            debug: {reason}
          </p>
        )}
      </div>
    </div>
  );
};

export default Footer;
